import './App.css';
import { useState, useEffect } from 'react';
import logo from './assets/images/dashboard/lepton-icono.png';
import dayjs from 'dayjs';
import Login from './containers/login';
import Header from './containers/header';
import MainScreen from './containers/mainScreen';
import { DateProvider } from './context/dateProvider';
import { fetchUserUpdate } from './utils/requests';
import { set } from 'lodash';

export const DEFAULT_USER_CONSTANTS = {
  userName: '',
  reports: { foundReports: false, startDate: dayjs().startOf('day'), endDate: dayjs().endOf('day'), mileageData: [], mileageTotalData: [], stoppedTimeData: [], stoppedTimeTotalData: [], speedViolationData: [], ralentiData: [], ralentiTotalData: [], fuelData: [], fuelTotalData: [], fuelLastFillings: {}, workingTimeTripsData: [], workingTimeEventsData: [], workingTimeTripsTotalData: {}, workingTimeEventsTotalData: {} },
  orderOfSections: ['mileage', 'stoppedTime', 'speedViolation', 'ralenti', 'fuel', 'workingTime'],
  speedLimit: 80,
  duplicatedSpeedLimit: 80,
  consumptionCar: 1.3,
  consumptionPickup: 1.5,
  consumptionTruck: 2,
  consCars: [],
  consPickups: [],
  consTrucks: [],
  magnaPrice: 24,
  premiumPrice: 25.7,
  dieselPrice: 26.2,
  magnaDevices: [],
  premiumDevices: [],
  dieselDevices: [],
  startTime: '22:00',
  endTime: '06:00',
};

const sections = [
  { id: 'mileage', name: 'Kilometraje', anchor: '#mileage_section', icon: 'readiness_score' },
  { id: 'stoppedTime', name: 'Tiempo detenido', anchor: '#stoppedTime_section', icon: 'timer_pause' },
  { id: 'speedViolation', name: 'Exceso de velocidad', anchor: '#speedViolation_section', icon: 'car_crash' },
  { id: 'ralenti', name: 'Ralentí', anchor: '#ralenti_section', icon: 'chronic' },
  { id: 'fuel', name: 'Combustible', anchor: '#fuel_section', icon: 'local_gas_station' },
  { id: 'workingTime', name: 'Tiempo de trabajo', anchor: '#workingTime_section', icon: 'time_engine' },
];

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [devices, setDevices] = useState([]);
  const [trackersGroups, setTrackersGroups] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [userHash, setUserHash] = useState(null);
  const [isMobileDevicesVisible, setIsMobileDevicesVisible] = useState(false); // Estado para mostrar u ocultar menú en móviles
  const [userConstants, setUserConstants] = useState(DEFAULT_USER_CONSTANTS);

  useEffect(() => {
    setUserConstants(DEFAULT_USER_CONSTANTS);
  }, [isAuthenticated]);

  // Función para actualizar userConstants y guardarlo en localStorage
  const updateUserConstants = async (newData) => {
    setUserConstants(prevState => {
      // 🧠 Clonar el estado anterior
      const updatedConstants = structuredClone(prevState);

      // 🔁 Convertir claves como "reports.mileageData" en objetos anidados
      for (const key in newData) {
        set(updatedConstants, key, newData[key]);
      }

      // 🌐 Llamada al backend con el formato plano original
      fetchUserUpdate(userHash, newData)
        .catch(error => console.error('Error al guardar en DB:', error));

      return updatedConstants;
    });
  };

  const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  };

  const getQueryParam = (param) => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(param);
  };

  useEffect(() => {
    let hash = getQueryParam('session_key');
    if (!hash) {
      hash = getCookie('session_key');
    }
    if (hash) {
      setUserHash(hash);
      setIsAuthenticated(true);
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    const handleLoad = () => {
      setIsLoading(false);
    };
    window.addEventListener('load', handleLoad);
    return () => {
      window.removeEventListener('load', handleLoad);
    };
  }, []);

  const handleLoginSuccess = (hash) => {
    setUserHash(hash);
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    setUserHash(null);
    setIsAuthenticated(false);
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
    });
  };



  const [sectionOrder, setSectionOrder] = useState(userConstants.orderOfSections
    .map(sectionId => sections.find(section => section.id === sectionId))
    .filter(Boolean));

  const setNewOrder = () => {
    setSectionOrder(userConstants.orderOfSections
      .map(sectionId => sections.find(section => section.id === sectionId))
      .filter(Boolean));
  }
  
  if (isLoading) {
    return (
      <div className="loading_overlay">
        <div className="lds-ring">
          <div></div>
          <div></div>
          <div></div>
          <div></div>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      {!isAuthenticated ? (
        <Login onLoginSuccess={handleLoginSuccess} />
      ) : (
        <>
          <Header
            onLogout={handleLogout}
            userHash={userHash}
            sectionOrder={sectionOrder}
            setSectionOrder={setSectionOrder}
            devices={devices}
            setDevices={setDevices}
            trackersGroups={trackersGroups}
            isMobileDevicesVisible={isMobileDevicesVisible}
            setIsMobileDevicesVisible={setIsMobileDevicesVisible}
            userConstants={userConstants}
            updateUserConstants={updateUserConstants}
            setUserConstants={setUserConstants}
          />
          <DateProvider>
            <MainScreen
              userHash={userHash}
              sectionOrder={sectionOrder}
              devices={devices}
              setDevices={setDevices}
              trackersGroups={trackersGroups}
              setTrackersGroups={setTrackersGroups}
              isMobileDevicesVisible={isMobileDevicesVisible}
              setIsMobileDevicesVisible={setIsMobileDevicesVisible}
              userConstants={userConstants}
              updateUserConstants={updateUserConstants}
              setNewOrder={setNewOrder}
            />
          </DateProvider>
          <button className='floating_button' onClick={scrollToTop}>
            <img src={logo} alt='Leptón logo'></img>
          </button>
        </>
      )}
    </div>
  );
}

export default App;
